// Utility Functions

import {
  STORAGE_KEYS,
  SURVEY,
  VITAL_SIGNS,
  RISK_LEVELS,
  MESSAGES,
  AUTH_EVENTS,
} from "../constants/app";
import type { Patient, VitalReading } from "../types";

// Local Storage Utilities
export const storage = {
  get: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  set: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Handle storage errors silently
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Handle storage errors silently
    }
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch {
      // Handle storage errors silently
    }
  },
};

// Authentication Utilities
export const auth = {
  isLoggedIn: (): boolean => {
    return storage.get(STORAGE_KEYS.IS_LOGGED_IN) === "true";
  },

  getUserEmail: (): string => {
    return storage.get(STORAGE_KEYS.USER_EMAIL) || "";
  },

  getUserId: (): string => {
    return storage.get(STORAGE_KEYS.USER_ID) || "";
  },

  getRole: (): 'admin' | 'super_admin' | null => {
    const role = storage.get(STORAGE_KEYS.ROLE);
    return role === 'admin' || role === 'super_admin' ? role : null;
  },

  isAdmin: (): boolean => {
    return storage.get(STORAGE_KEYS.ROLE) === 'super_admin';
  },

  getHospitalName: (): string => {
    return storage.get(STORAGE_KEYS.HOSPITAL_NAME) || "";
  },

  getAuthToken: (): string | null => {
    return storage.get(STORAGE_KEYS.AUTH_TOKEN);
  },

  saveUserSession: (
    email: string,
    role: 'admin' | 'super_admin',
    hospitalName: string,
    userId: string,
    token?: string
  ): void => {
    storage.set(STORAGE_KEYS.IS_LOGGED_IN, "true");
    storage.set(STORAGE_KEYS.USER_EMAIL, email);
    storage.set(STORAGE_KEYS.ROLE, role);
    storage.set(STORAGE_KEYS.HOSPITAL_NAME, hospitalName);
    storage.set(STORAGE_KEYS.USER_ID, userId);
    if (token) {
      storage.set(STORAGE_KEYS.AUTH_TOKEN, token);
    }
  },

  clearUserSession: (): void => {
    Object.values(STORAGE_KEYS).forEach((key) => storage.remove(key));
  },
};

// Validation Utilities
export const validation = {
  isValidEmail: (email: string): boolean => {
    return SURVEY.VALIDATION.EMAIL_REGEX.test(email);
  },

  isRequired: (value: string): boolean => {
    return value.trim().length > 0;
  },

  hasMinLength: (value: string, minLength: number): boolean => {
    return value.length >= minLength;
  },

  hasMaxLength: (value: string, maxLength: number): boolean => {
    return value.length <= maxLength;
  },

  isNumeric: (value: string): boolean => {
    return /^\d+$/.test(value);
  },

  isValidAge: (age: string): boolean => {
    const numAge = parseInt(age);
    return validation.isNumeric(age) && numAge > 0 && numAge <= 120;
  },

  isValidWeight: (weight: string): boolean => {
    const numWeight = parseFloat(weight);
    return !isNaN(numWeight) && numWeight > 0;
  },
};

// Conversion Utilities
export const conversions = {
  calculateBMI: (heightCm: number, weightKg: number): number => {
    const heightM = heightCm / 100;
    return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
  },

  formatWeight: (weightKg: number): string => {
    return `${Math.round(weightKg * 10) / 10} kg`;
  },
};

// Date and Time Utilities
export const dateTime = {
  formatTimestamp: (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } catch {
      return "Invalid Date";
    }
  },

  formatDate: (date: Date | string): string => {
    try {
      const d = new Date(date);
      return d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  },

  formatDateTime: (date: Date | string): string => {
    try {
      const d = new Date(date);
      return `${dateTime.formatDate(d)}, ${dateTime.formatTimestamp(
        d.toISOString()
      )}`;
    } catch {
      return "Invalid Date";
    }
  },

  isToday: (date: Date | string): boolean => {
    try {
      const d = new Date(date);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    } catch {
      return false;
    }
  },

  getAge: (dob: string): number => {
    try {
      let day: number, month: number, year: number;

      if (dob.includes("-")) {
        const parts = dob.split("-");
        if (parts.length === 3) {
          year = parseInt(parts[0]);
          month = parseInt(parts[1]);
          day = parseInt(parts[2]);
        } else {
          return 0;
        }
      } else {
        const parts = dob.split("/");
        if (parts.length === 3) {
          day = parseInt(parts[0]);
          month = parseInt(parts[1]);
          year = parseInt(parts[2]);
        } else {
          return 0;
        }
      }

      if (!year) return 0;
      const today = new Date();
      let age = today.getFullYear() - year;
      if (
        today.getMonth() + 1 < month ||
        (today.getMonth() + 1 === month && today.getDate() < day)
      ) {
        age--;
      }
      return age;
    } catch {
      return 0;
    }
  },
};

// Patient Data Utilities
export const patientUtils = {
  getFullName: (patient: Patient): string => {
    return patient.personal_information?.full_name || "Unknown";
  },

  getAge: (patient: Patient): number => {
    return dateTime.getAge(patient.personal_information?.dob || "");
  },

  getGender: (patient: Patient): string => {
    return patient.personal_information?.gender || "Unknown";
  },

  getRiskLevel: (patient: Patient): string => {
    return patient.score_summary?.risk_level || "unknown";
  },

  getRiskScore: (patient: Patient): number => {
    return patient.score_summary?.risk_score || 0;
  },

  getLatestVital: (patient: Patient, bodyType: string): VitalReading | null => {
    if (!patient.vitals) return null;
    let all: VitalReading[] = [];
    Object.values(patient.vitals).forEach((arr: any) => {
      if (Array.isArray(arr)) {
        all = all.concat(arr.filter((v: any) => v.bodyType === bodyType));
      }
    });
    if (all.length === 0) return null;
    all.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return all[0];
  },

  getLatestHeartRate: (patient: Patient): number | null => {
    const vital = patientUtils.getLatestVital(patient, VITAL_SIGNS.HEART_RATE);
    return vital?.body?.value || null;
  },

  getLatestBloodPressure: (
    patient: Patient
  ): { systolic: number; diastolic: number } | null => {
    const vital = patientUtils.getLatestVital(
      patient,
      VITAL_SIGNS.BLOOD_PRESSURE
    );
    if (vital?.body?.systolic && vital?.body?.diastolic) {
      return {
        systolic: vital.body.systolic,
        diastolic: vital.body.diastolic,
      };
    }
    return null;
  },

  getLatestBloodOxygen: (patient: Patient): number | null => {
    const vital = patientUtils.getLatestVital(
      patient,
      VITAL_SIGNS.BLOOD_OXYGEN
    );
    return vital?.body?.value || null;
  },

  filterByHospital: (
    patients: Patient[],
    userId: string,
    isAdmin: boolean
  ): Patient[] => {
    if (isAdmin) return patients;
    return patients.filter((patient) => patient.hospital_id === userId);
  },
};

// Vital Signs Utilities
export const vitalUtils = {
  getHeartRateColor: (heartRate: number): string => {
    const { MIN, MAX } = VITAL_SIGNS.RANGES.HEART_RATE;
    if (heartRate < MIN || heartRate > MAX) {
      return "text-red-500";
    }
    return "text-green-500";
  },

  getBloodPressureColor: (systolic: number, diastolic: number): string => {
    const {
      SYSTOLIC_HIGH,
      DIASTOLIC_HIGH,
      SYSTOLIC_ELEVATED,
      DIASTOLIC_ELEVATED,
    } = VITAL_SIGNS.RANGES.BLOOD_PRESSURE;
    if (systolic >= SYSTOLIC_HIGH || diastolic >= DIASTOLIC_HIGH) {
      return "text-red-500";
    }
    if (systolic >= SYSTOLIC_ELEVATED || diastolic >= DIASTOLIC_ELEVATED) {
      return "text-yellow-500";
    }
    return "text-green-500";
  },

  getBloodOxygenColor: (oxygen: number): string => {
    const { MIN, CRITICAL } = VITAL_SIGNS.RANGES.BLOOD_OXYGEN;
    if (oxygen < CRITICAL) {
      return "text-red-500";
    }
    if (oxygen < MIN) {
      return "text-yellow-500";
    }
    return "text-green-500";
  },

  isHeartRateNormal: (heartRate: number): boolean => {
    const { MIN, MAX } = VITAL_SIGNS.RANGES.HEART_RATE;
    return heartRate >= MIN && heartRate <= MAX;
  },

  isBloodPressureNormal: (systolic: number, diastolic: number): boolean => {
    const { SYSTOLIC_ELEVATED, DIASTOLIC_ELEVATED } =
      VITAL_SIGNS.RANGES.BLOOD_PRESSURE;
    return systolic < SYSTOLIC_ELEVATED && diastolic < DIASTOLIC_ELEVATED;
  },

  isBloodOxygenNormal: (oxygen: number): boolean => {
    return oxygen >= VITAL_SIGNS.RANGES.BLOOD_OXYGEN.MIN;
  },
};

// Risk Assessment Utilities
export const riskUtils = {
  determineRiskLevel: (score: number): string => {
    const { LOW, MEDIUM } = RISK_LEVELS.THRESHOLDS;
    if (score <= LOW) return RISK_LEVELS.LOW;
    if (score <= MEDIUM) return RISK_LEVELS.MEDIUM;
    return RISK_LEVELS.HIGH;
  },

  getRiskBadgeClass: (riskLevel: string): string => {
    switch (riskLevel.toLowerCase()) {
      case RISK_LEVELS.HIGH:
        return "bg-red-100 text-red-800";
      case RISK_LEVELS.MEDIUM:
        return "bg-yellow-100 text-yellow-800";
      case RISK_LEVELS.LOW:
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  },

  getRiskDisplay: (riskLevel: string) => {
    const normalized = riskLevel?.toLowerCase() || "";
    if (normalized === "poor" || normalized === "high") {
      return {
        label: "High" as const,
        badgeClass: "bg-risk-high text-white",
        vitalTextClass: "text-vital-high",
      };
    }
    if (normalized === "average" || normalized === "medium") {
      return {
        label: "Medium" as const,
        badgeClass: "bg-risk-medium text-white",
        vitalTextClass: "text-vital-medium",
      };
    }
    if (normalized === "good" || normalized === "low") {
      return {
        label: "Low" as const,
        badgeClass: "bg-risk-low text-white",
        vitalTextClass: "text-vital-low",
      };
    }
    return {
      label: riskLevel
        ? riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1).toLowerCase()
        : "-",
      badgeClass: "bg-gray-500 text-white",
      vitalTextClass: "text-gray-500",
    };
  },

  formatSleepDuration: (value: unknown): string => {
    if (value === null || value === undefined || value === "") return "-";
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed || trimmed === "-") return "-";
      if (/^\d+h\s*\d*\s*min?$/i.test(trimmed) || /^\d+h$/i.test(trimmed)) {
        return trimmed.replace(/\s*min$/i, "min");
      }
      const asNum = parseFloat(trimmed);
      if (!Number.isNaN(asNum)) {
        return riskUtils.formatSleepDuration(asNum);
      }
      return trimmed;
    }
    if (typeof value === "number" && !Number.isNaN(value)) {
      const totalMinutes = Math.round(value * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours === 0) return `${minutes}min`;
      if (minutes === 0) return `${hours}h`;
      return `${hours}h ${minutes}min`;
    }
    return "-";
  },

  getRiskConfig: (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case RISK_LEVELS.HIGH:
        return {
          logo: "/images/High_logo.png",
          bgColor: "#EF4444",
          message:
            "High cardiovascular risk detected. Immediate medical attention recommended.",
        };
      case RISK_LEVELS.MEDIUM:
        return {
          logo: "/images/Medium_logo.png",
          bgColor: "#F59E0B",
          message:
            "Moderate cardiovascular risk. Regular monitoring and lifestyle changes recommended.",
        };
      case RISK_LEVELS.LOW:
        return {
          logo: "/images/Low_logo.png",
          bgColor: "#14B8A6",
          message:
            "Low cardiovascular risk. Continue maintaining healthy lifestyle.",
        };
      default:
        return {
          logo: "/images/High_logo.png",
          bgColor: "#6B7280",
          message: "Risk assessment unavailable.",
        };
    }
  },
};

// Array Utilities
export const arrayUtils = {
  isEmpty: (arr: any[]): boolean => {
    return !arr || arr.length === 0;
  },

  isNotEmpty: (arr: any[]): boolean => {
    return arr && arr.length > 0;
  },

  chunk: <T>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  },

  unique: <T>(array: T[], keyFn?: (item: T) => any): T[] => {
    if (!keyFn) {
      return Array.from(new Set(array));
    }
    const seen = new Set();
    return array.filter((item) => {
      const key = keyFn(item);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  },

  sortBy: <T>(
    array: T[],
    keyFn: (item: T) => any,
    direction: "asc" | "desc" = "asc"
  ): T[] => {
    return [...array].sort((a, b) => {
      const aVal = keyFn(a);
      const bVal = keyFn(b);
      if (direction === "desc") {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    });
  },
};

// String Utilities
export const stringUtils = {
  capitalize: (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  },

  truncate: (str: string, length: number, suffix: string = "..."): string => {
    if (str.length <= length) return str;
    return str.slice(0, length) + suffix;
  },

  slugify: (str: string): string => {
    return str
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  },

  isEmpty: (str: string): boolean => {
    return !str || str.trim().length === 0;
  },

  isNotEmpty: (str: string): boolean => {
    return Boolean(str && str.trim().length > 0);
  },

  formatName: (firstName: string, lastName: string): string => {
    return [firstName, lastName].filter(Boolean).join(" ");
  },

  extractInitials: (name: string): string => {
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  },
};

// Messages that indicate the current auth token is expired/invalid (as
// opposed to a login attempt with bad credentials).
const SESSION_EXPIRED_HINTS = [
  "token expired",
  "not authenticated",
  "authentication required",
  "invalid token",
  "unauthorized",
];

// Guards against a burst of parallel 401s dispatching the event many times.
let sessionExpiryDispatched = false;

// Detect an expired/invalid session from a 401 and, when the user is currently
// authenticated, clear the session and notify the app to redirect to Login.
// Returns true when the response was handled as a session expiry.
const handleSessionExpiry = (status: number, errorMessage: string): boolean => {
  if (status !== 401) return false;

  // Only treat as session expiry when a token is actually stored. During login
  // there is no stored token, so invalid-credential 401s are excluded.
  const hasToken = Boolean(auth.getAuthToken()) || auth.isLoggedIn();
  if (!hasToken) return false;

  const message = (errorMessage || "").toLowerCase();
  const looksLikeTokenFailure =
    message === "" ||
    SESSION_EXPIRED_HINTS.some((hint) => message.includes(hint));
  if (!looksLikeTokenFailure) return false;

  auth.clearUserSession();

  if (!sessionExpiryDispatched && typeof window !== "undefined") {
    sessionExpiryDispatched = true;
    window.dispatchEvent(new CustomEvent(AUTH_EVENTS.SESSION_EXPIRED));
    // Allow future expiries (e.g. after re-login) to dispatch again.
    setTimeout(() => {
      sessionExpiryDispatched = false;
    }, 1000);
  }

  return true;
};

// API Utilities
export const apiUtils = {
  handleResponse: async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle backend error format: { success: false, error: "message" }
      let errorMessage = errorData.error || errorData.message;

      // Handle expired/invalid session globally: clear auth and trigger the
      // redirect-to-login flow, then throw a neutral message so no raw
      // "Token expired" string surfaces in any component.
      if (handleSessionExpiry(response.status, errorMessage)) {
        throw new Error(MESSAGES.AUTH.SESSION_EXPIRED);
      }
      
      // Handle rate limiting (429) - backend may return message directly or in error field
      if (response.status === 429) {
        errorMessage = errorMessage || errorData.message || 'Too many authentication attempts. Please try again later.';
      }
      
      // Handle authentication errors (401) - ensure consistent message
      if (response.status === 401 && !errorMessage) {
        errorMessage = 'Invalid email or password';
      }
      
      // Fallback to status code if no error message found
      if (!errorMessage) {
        errorMessage = `HTTP ${response.status}`;
      }
      
      throw new Error(errorMessage);
    }
    return response.json();
  },

  // For callers that read the response body directly (e.g. multipart uploads)
  // instead of going through handleResponse. Detects an expired/invalid session
  // and, when found, clears auth, triggers the redirect flow, and throws the
  // neutral session-expired error. Returns false otherwise.
  throwIfSessionExpired: (status: number, errorMessage?: string): boolean => {
    if (handleSessionExpiry(status, errorMessage || "")) {
      throw new Error(MESSAGES.AUTH.SESSION_EXPIRED);
    }
    return false;
  },

  // Drop-in replacement for fetch on authenticated requests. Any 401 that
  // indicates an expired/invalid session clears auth, triggers the global
  // redirect-to-login flow, and throws a neutral error - regardless of how the
  // caller reads the response. Non-session responses are returned unchanged.
  apiFetch: async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const response = await fetch(input, init);
    if (response.status === 401) {
      const errData = await response
        .clone()
        .json()
        .catch(() => ({} as any));
      if (
        handleSessionExpiry(
          response.status,
          errData?.error || errData?.message || ""
        )
      ) {
        throw new Error(MESSAGES.AUTH.SESSION_EXPIRED);
      }
    }
    return response;
  },

  createRequestOptions: (method: string, body?: any): RequestInit => {
    const options: RequestInit = {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include", // Include cookies for authentication
    };

    // Add Authorization header with token from localStorage as fallback
    // This allows authentication to work in incognito mode when cookies are blocked
    const token = auth.getAuthToken();
    if (token) {
      options.headers = {
        ...options.headers,
        "Authorization": `Bearer ${token}`,
      };
    }

    if (body) {
      options.body = JSON.stringify(body);
    }

    return options;
  },

  withTimeout: <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), timeoutMs)
      ),
    ]);
  },
};

// Debounce Utility
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Throttle Utility
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
};

// Error Handling Utilities
export const errorUtils = {
  getErrorMessage: (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string") {
      return error;
    }
    return "An unknown error occurred";
  },

  logError: (error: unknown, context?: string): void => {
    console.error(context ? `[${context}]` : "", error);
  },

  isNetworkError: (error: unknown): boolean => {
    if (error instanceof Error) {
      return (
        error.message.toLowerCase().includes("network") ||
        error.message.toLowerCase().includes("fetch") ||
        error.message.toLowerCase().includes("timeout")
      );
    }
    return false;
  },
};
